/**
 * TryPage (`/try`) — Anonymous "Try Contract Review" funnel.
 *
 * Flow:
 *   1. /try          — minimal upload page, no auth, drag-or-click, Analyze CTA.
 *   2. in-page       — Multi-agent processing screen animates for ~6s while the
 *                      real /api/trial/analyze call runs in parallel.
 *   3. /try/results  — partial verdict + 2 top risks + stats, gated by an
 *                      inline mini-waitlist form. Submitting unlocks the full
 *                      report (emailed) and routes the user to /waitlist with
 *                      `state.from = "trial"` so the team-invite mechanic kicks in.
 *
 * Document text is extracted client-side (PDF / DOCX use existing helpers
 * in lib/docExtract; .txt is read directly) so the backend only ever sees
 * plain UTF-8 — and the user's file never leaves the browser.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Loader2, Lock, Upload } from 'lucide-react';

import { cn } from '@/lib/utils';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const BACKEND = getBackendBaseUrl();

const PERSONAL = new Set([
  'gmail.com', 'googlemail.com', 'hotmail.com', 'live.com', 'outlook.com',
  'msn.com', 'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'icloud.com', 'me.com',
  'mac.com', 'aol.com', 'protonmail.com', 'proton.me', 'mail.com', 'gmx.com',
  'zoho.com', 'qq.com', '163.com', '126.com',
]);

const isCorporate = (em) => {
  const d = (em.split('@')[1] || '').trim().toLowerCase();
  return d && !PERSONAL.has(d);
};

// Strip extension + truncate so the filename displays cleanly in headers.
const cleanDocumentName = (filename) => {
  if (!filename) return 'document';
  let name = String(filename).replace(/\.(docx?|pdf|txt)$/i, '');
  if (name.length > 50) name = name.substring(0, 50) + '…';
  return name;
};

// Format the extraction-stats chip below the document title.
// Returns null if no useful data; otherwise an array of human strings.
const formatExtractionParts = (extraction, isArabic) => {
  if (!extraction) return null;
  const parts = [];
  const num = (n) => Number(n || 0).toLocaleString(isArabic ? 'ar-EG' : 'en-US');
  if (extraction.characters) {
    parts.push(isArabic
      ? `${num(extraction.characters)} حرف`
      : `${num(extraction.characters)} characters`);
  }
  if (extraction.tables && extraction.tables > 0) {
    parts.push(isArabic
      ? `${num(extraction.tables)} ${extraction.tables === 1 ? 'جدول' : 'جداول'}`
      : `${num(extraction.tables)} ${extraction.tables === 1 ? 'table' : 'tables'}`);
  }
  // Prefer "sections" if backend supplied it; otherwise fall back to paragraphs.
  const sectionCount = extraction.sections || extraction.paragraphs || 0;
  if (sectionCount > 0) {
    parts.push(isArabic
      ? `${num(sectionCount)} ${sectionCount === 1 ? 'قسم' : 'أقسام'}`
      : `${num(sectionCount)} ${sectionCount === 1 ? 'section' : 'sections'}`);
  }
  if (parts.length === 0) return null;
  return parts;
};

// ── Public layout shell (no sidebar, no auth) ───────────────────────
function TrialShell({ children }) {
  return (
    <div
      data-testid="trial-shell"
      dir="ltr"
      className="min-h-screen w-full bg-[#F5F3EF] text-[#0A0A0A]"
      style={{ fontFamily: 'Inter, ui-sans-serif, system-ui' }}
    >
      <header className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="text-[15px] font-semibold tracking-tight">Bayenah</Link>
        <Link to="/chat" className="text-[12.5px] text-[#666] hover:text-[#0A0A0A]">Sign in</Link>
      </header>
      {children}
    </div>
  );
}

// ── Upload page ─────────────────────────────────────────────────────
// ── Trial session (email-first gate) ────────────────────────────────
// The visitor gives an email once; we hold the returned trial_id in
// sessionStorage so a refresh or a bounce to /try/results and back
// doesn't ask again. Cleared only by closing the tab.
const TRIAL_KEY = 'bayenah.trial_session';

const readTrialSession = () => {
  try {
    const raw = sessionStorage.getItem(TRIAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeTrialSession = (session) => {
  try { sessionStorage.setItem(TRIAL_KEY, JSON.stringify(session)); } catch { /* private mode */ }
};

function EmailGate({ onStarted }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!isCorporate(value)) {
      setError('Please use your work email address.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND}/api/trial/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ work_email: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not start your trial.');
      writeTrialSession(data);
      onStarted(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-xl mx-auto px-6 pt-16 pb-24" data-testid="trial-email-gate">
      <h1 className="text-[34px] leading-[1.1] font-semibold tracking-[-0.03em]">
        Try Bayenah free.
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-[#555]">
        Draft a contract or review one of your own — grounded in Saudi law.
        Enter your work email to begin. No account, no card.
      </p>

      <form onSubmit={submit} className="mt-8">
        <label htmlFor="trial-email" className="sr-only">Work email</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            id="trial-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            placeholder="you@company.com"
            data-testid="trial-email-input"
            className="flex-1 h-12 px-4 rounded-md border border-[#DDD9D0] bg-white text-[15px]
                       outline-none focus:border-[#0A0A0A] transition-colors"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="trial-email-submit"
            className="h-12 px-6 rounded-md bg-[#0A0A0A] text-white text-[14px] font-medium
                       inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
        {error && (
          <p role="alert" className="mt-3 text-[13px] text-[#B3261E]" data-testid="trial-email-error">
            {error}
          </p>
        )}
      </form>

      <p className="mt-6 text-[12.5px] text-[#8A8A8A]">
        Your document is processed securely and never used to train AI models.
      </p>
    </main>
  );
}

// ── Choose what to try ──────────────────────────────────────────────
function ModeChooser({ remaining, onPick }) {
  const opts = [
    {
      id: 'draft',
      icon: FileText,
      title: 'Draft a contract',
      body: 'Describe the deal in plain English or Arabic. Get a structured contract grounded in Saudi law.',
      left: remaining?.draft ?? 1,
    },
    {
      id: 'review',
      icon: Upload,
      title: 'Review a contract',
      body: "Upload an agreement you already have. See the risks and compliance gaps it's carrying.",
      left: remaining?.review ?? 1,
    },
  ];

  return (
    <main className="max-w-3xl mx-auto px-6 pt-14 pb-24" data-testid="trial-mode-chooser">
      <h1 className="text-[30px] leading-[1.12] font-semibold tracking-[-0.03em]">
        What would you like to try?
      </h1>
      <p className="mt-3 text-[14.5px] text-[#555]">One free draft and one free review.</p>

      <div className="mt-9 grid sm:grid-cols-2 gap-4">
        {opts.map(({ id, icon: Icon, title, body, left }) => {
          const spent = left <= 0;
          return (
            <button
              key={id}
              type="button"
              disabled={spent}
              onClick={() => onPick(id)}
              data-testid={`trial-mode-${id}`}
              className={cn(
                'text-left p-6 rounded-lg border bg-white transition',
                spent
                  ? 'border-[#E5E1D8] opacity-55 cursor-not-allowed'
                  : 'border-[#DDD9D0] hover:border-[#0A0A0A]',
              )}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <div className="mt-3 text-[17px] font-semibold tracking-[-0.02em]">{title}</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#666]">{body}</p>
              <div className="mt-4 text-[11.5px] uppercase tracking-[0.12em] text-[#8A8A8A]">
                {spent ? 'Already used' : `${left} free`}
              </div>
            </button>
          );
        })}
      </div>
    </main>
  );
}

// ── Request full access (shared by both result views) ───────────────
function RequestAccess({ trialId, onDone }) {
  const [form, setForm] = useState({ full_name: '', company_name: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Sep 2026 (funnel bundle) — Request-access form is now hidden by
  // default; the toggle button reveals it as a secondary option.
  const [showForm, setShowForm] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2 || form.company_name.trim().length < 2) {
      setError('Please enter your name and organization.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND}/api/trial/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trial_id: trialId, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not submit your request.');
      onDone(data);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 p-6 rounded-lg border border-[#DDD9D0] bg-white" data-testid="trial-post-cta">
      {/* Sep 2026 (funnel bundle) — primary CTA is now "Continue with
          Google" routed to /signin (sign-up is open). "Request access"
          demoted to a secondary link so users on the fence still have
          a low-commitment path. */}
      <div className="text-[17px] font-semibold tracking-[-0.02em]">Try the full platform</div>
      <p className="mt-1 text-[13.5px] text-[#666]">Full platform, free during the preview.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a
          href="/signin"
          data-testid="trial-cta-continue-google"
          className="h-11 px-6 rounded-md bg-[#0A0A0A] text-white text-[14px] font-medium inline-flex items-center gap-2"
        >
          Continue with Google <ArrowRight className="w-4 h-4" />
        </a>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          data-testid="trial-cta-request-access-toggle"
          className="text-[13px] text-[#666] underline underline-offset-2 hover:text-[#0A0A0A]"
        >
          Request access
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-6 pt-6 border-t border-[#DDD9D0]" data-testid="trial-request-access">
          <div className="mt-1 grid sm:grid-cols-2 gap-3">
            <input
              value={form.full_name}
              onChange={(e) => { setForm({ ...form, full_name: e.target.value }); setError(''); }}
              placeholder="Full name"
              data-testid="trial-ra-name"
              className="h-11 px-3.5 rounded-md border border-[#DDD9D0] text-[14px] outline-none focus:border-[#0A0A0A]"
            />
            <input
              value={form.company_name}
              onChange={(e) => { setForm({ ...form, company_name: e.target.value }); setError(''); }}
              placeholder="Organization"
              data-testid="trial-ra-company"
              className="h-11 px-3.5 rounded-md border border-[#DDD9D0] text-[14px] outline-none focus:border-[#0A0A0A]"
            />
          </div>
          {error && <p role="alert" className="mt-3 text-[13px] text-[#B3261E]">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="trial-ra-submit"
            className="mt-4 h-11 px-6 rounded-md border border-[#0A0A0A] text-[#0A0A0A] text-[14px] font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send request <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Draft a contract ────────────────────────────────────────────────
function DraftPanel({ trial, setTrial, onBack }) {
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [requested, setRequested] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (brief.trim().length < 20) {
      setError('Please describe the contract in a little more detail.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND}/api/trial/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trial_id: trial.trial_id, brief: brief.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not draft the contract.');
      setResult(data);
      if (data.remaining) {
        const next = { ...trial, remaining: data.remaining };
        setTrial(next);
        writeTrialSession(next);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const isAr = result.language === 'arabic';
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-24" data-testid="trial-draft-result">
        <div className="text-[11.5px] uppercase tracking-[0.14em] text-[#8A8A8A]">
          {result.contract_type || 'Draft'}
          {result.corpus_verified && ` · ${result.sources_count} legal sources`}
        </div>
        <h1 className="mt-3 text-[28px] leading-[1.15] font-semibold tracking-[-0.03em]">
          {result.title || 'Your draft'}
        </h1>

        <div className="mt-8 space-y-6" dir={isAr ? 'rtl' : 'ltr'}>
          {result.articles.map((a, i) => (
            <div key={i}>
              <div className="text-[15px] font-semibold tracking-[-0.015em]">{a.heading}</div>
              <p className="mt-2 text-[14px] leading-[1.65] text-[#333] whitespace-pre-wrap">{a.body}</p>
            </div>
          ))}
        </div>

        {result.gated && (
          <div className="mt-8 p-5 rounded-lg bg-[#EFEDE6] border border-[#DDD9D0]" data-testid="trial-draft-gated">
            <div className="flex items-center gap-2 text-[13px] font-medium">
              <Lock className="w-4 h-4" strokeWidth={1.5} />
              {(() => {
                const n = result.articles_total - result.articles.length;
                return `${n} more ${n === 1 ? 'article' : 'articles'} in the full draft`;
              })()}
            </div>
            <ul className="mt-3 space-y-1.5">
              {result.withheld_headings.map((h, i) => (
                <li key={i} className="text-[13.5px] text-[#666]">{h.heading}</li>
              ))}
            </ul>
          </div>
        )}

        {result.notes?.length > 0 && (
          <div className="mt-6">
            <div className="text-[11.5px] uppercase tracking-[0.14em] text-[#8A8A8A]">Assumptions</div>
            <ul className="mt-2 space-y-1.5">
              {result.notes.map((n, i) => (
                <li key={i} className="text-[13.5px] text-[#666]">— {n}</li>
              ))}
            </ul>
          </div>
        )}

        {requested ? (
          <div className="mt-10 p-6 rounded-lg border border-[#DDD9D0] bg-white" data-testid="trial-ra-done">
            <div className="text-[16px] font-semibold tracking-[-0.02em]">Request received</div>
            <p className="mt-2 text-[13.5px] text-[#666]">
              We'll be in touch at {trial.email} once your access is enabled.
            </p>
          </div>
        ) : (
          <RequestAccess trialId={trial.trial_id} onDone={() => setRequested(true)} />
        )}

        <button
          type="button"
          onClick={onBack}
          className="mt-8 text-[13px] text-[#666] hover:text-[#0A0A0A]"
          data-testid="trial-draft-back"
        >
          ← Back
        </button>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto px-6 pt-14 pb-24" data-testid="trial-draft-form">
      <h1 className="text-[30px] leading-[1.12] font-semibold tracking-[-0.03em]">
        Describe the contract.
      </h1>
      <p className="mt-3 text-[14.5px] leading-relaxed text-[#555]">
        Plain English or Arabic — the parties, the deal, anything that matters.
        Bayenah handles the structure and the Saudi-law grounding.
      </p>

      <form onSubmit={submit} className="mt-8">
        <label htmlFor="trial-brief" className="sr-only">Contract brief</label>
        <textarea
          id="trial-brief"
          rows={7}
          autoFocus
          value={brief}
          onChange={(e) => { setBrief(e.target.value); setError(''); }}
          placeholder="e.g. A mutual NDA between a Riyadh software company and a UK supplier, covering a 2-year evaluation period, with Saudi law governing and arbitration in Riyadh."
          data-testid="trial-brief-input"
          className="w-full p-4 rounded-md border border-[#DDD9D0] bg-white text-[14.5px] leading-relaxed
                     outline-none focus:border-[#0A0A0A] resize-y transition-colors"
        />
        {error && <p role="alert" className="mt-3 text-[13px] text-[#B3261E]" data-testid="trial-draft-error">{error}</p>}
        <div className="mt-4 flex items-center gap-4">
          <button
            type="submit"
            disabled={busy}
            data-testid="trial-draft-submit"
            className="h-12 px-6 rounded-md bg-[#0A0A0A] text-white text-[14px] font-medium
                       inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</>
              : <>Draft it <ArrowRight className="w-4 h-4" /></>}
          </button>
          <button type="button" onClick={onBack} className="text-[13px] text-[#666] hover:text-[#0A0A0A]">
            ← Back
          </button>
        </div>
      </form>
    </main>
  );
}

export default function TryPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [trial, setTrial] = useState(() => readTrialSession());
  const [mode, setMode] = useState(null);   // null | 'draft' | 'review'
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hover, setHover] = useState(false);

  // Processing-screen state (Fix 6)
  const [processing, setProcessing] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [apiError, setApiError] = useState('');

  // Live extraction state (Feb 2026): the moment a file lands we
  // pre-extract its text + classify the contract type so the upload
  // page can show a "Document type detected: X" chip without waiting
  // for the user to click Analyze. The result is cached and reused
  // when submit() actually fires the /api/trial/analyze call.
  const [extraction, setExtraction] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [detectedType, setDetectedType] = useState('');
  const [detectedLanguage, setDetectedLanguage] = useState('');

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError('File exceeds 10 MB.');
      return;
    }
    if (!/\.(pdf|docx?|txt)$/i.test(f.name)) {
      setError('Please upload a PDF, DOCX, or TXT file.');
      return;
    }
    setError('');
    // Reset any prior extraction state when a new file is picked.
    setExtraction(null);
    setExtractedText('');
    setDetectedType('');
    setDetectedLanguage('');
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    handleFiles(e.dataTransfer.files);
  };

  const extractText = async (f) => {
    setExtracting(true);
    try {
      // .txt files: read directly. Compute simple stats client-side so
      // the transparency chip still renders. (No backend contract-type
      // classification for TXT — the upload-page chip will only show
      // stats; type is filled in later by /api/trial/analyze.)
      if (/\.txt$/i.test(f.name)) {
        const text = await f.text();
        setExtraction({ characters: text.length, paragraphs: text.split(/\n+/).filter(Boolean).length });
        // Heuristic language detection on TXT path so the chip flips RTL
        // when the document is Arabic.
        const arabicRatio = (text.match(/[\u0600-\u06FF]/g) || []).length / Math.max(text.trim().length, 1);
        setDetectedLanguage(arabicRatio > 0.3 ? 'arabic' : 'english');
        return text;
      }
      // PDF / DOCX: backend extraction is the ONLY safe path. We never
      // fall back to `f.text()` for binary files — that would forward
      // raw ZIP bytes as the contract text and produce a hallucinated
      // review (the original bug, Feb 2026). If the backend fails, we
      // surface a clear error so the user can retry as PDF/TXT.
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${BACKEND}/api/trial/extract-text`, { method: 'POST', body: fd });
      if (!r.ok) {
        let detail = '';
        try { detail = (await r.json())?.detail || ''; } catch { /* noop */ }
        throw new Error(detail || "Couldn't read your file. Please try a TXT or text-extractable PDF.");
      }
      const data = await r.json();
      if (data?.extraction) setExtraction(data.extraction);
      if (data?.contract_type) setDetectedType(data.contract_type);
      if (data?.language) setDetectedLanguage(data.language);
      return data.text || '';
    } finally {
      setExtracting(false);
    }
  };

  // Auto-extract the moment a file is picked. Cancellable via cancelled
  // flag so a rapid drag-drop replacement doesn't race the previous
  // extraction. Errors are caught silently — submit() will surface a
  // proper error if the user proceeds with a broken file.
  useEffect(() => {
    if (!file || submitting || processing) return;
    let cancelled = false;
    (async () => {
      try {
        const text = await extractText(file);
        if (!cancelled) setExtractedText(text || '');
      } catch (e) {
        if (!cancelled) {
          setExtractedText('');
          // Surface friendly message inline; the dropzone error slot
          // already renders `error`.
          setError(e?.message || "Couldn't read your file. Please try TXT or text-extractable PDF.");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const submit = async () => {
    if (!file || submitting) return;
    setError('');
    setSubmitting(true);
    setApiResult(null);
    setApiError('');

    // Extract text FIRST (so we know we have something to send), then
    // flip into the processing screen immediately and fire the API in
    // parallel — the animation runs while the real call resolves.
    try {
      // Reuse cached extraction if the user already uploaded the file
      // (auto-extract effect runs the moment file is picked). Falls
      // back to a fresh extractText() if the cache is empty for any
      // reason (e.g. extraction was still in flight when they clicked).
      const text = extractedText || await extractText(file);
      if (!text || text.trim().length < 80) {
        throw new Error("Couldn't read your file. Please try a TXT or text-extractable PDF.");
      }

      setProcessing(true); // kick the animation

      // Fire the real API call; don't await here — let ProcessingScreen
      // observe apiResult/apiError via props.
      fetch(`${BACKEND}/api/trial/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_text: text.slice(0, 50000),
          document_name: file.name,
          // Pre-detected by /api/trial/extract-text — lets the analyze
          // endpoint inject a type-specific checklist into its prompt.
          ...(detectedType ? { contract_type: detectedType } : {}),
          // Email-first funnel: budgets this review against the lead's
          // allowance instead of the bare per-IP limiter.
          ...(trial?.trial_id ? { trial_id: trial.trial_id } : {}),
        }),
      })
        .then(async (r) => {
          const data = await r.json();
          if (!r.ok) throw new Error(data?.detail || 'Analysis failed.');
          setApiResult(data);
        })
        .catch((e) => {
          setApiError(e.message || 'Could not analyze the contract.');
        });
    } catch (e) {
      setError(e.message || 'Could not analyze the contract. Please try again.');
      setSubmitting(false);
    }
  };

  const onProcessingDone = useCallback(() => {
    if (apiError) {
      setProcessing(false);
      setSubmitting(false);
      setError(apiError);
      return;
    }
    if (apiResult) {
      navigate('/try/results', { state: { result: apiResult, document_name: file?.name, extraction } });
    }
  }, [apiError, apiResult, navigate, file, extraction]);

  // Email gate comes first: no trial session, no upload UI.
  if (!trial?.trial_id) {
    return (
      <TrialShell>
        <EmailGate onStarted={setTrial} />
      </TrialShell>
    );
  }

  // Then pick a capability. Review falls through to the upload flow below.
  if (!mode) {
    return (
      <TrialShell>
        <ModeChooser remaining={trial.remaining} onPick={setMode} />
      </TrialShell>
    );
  }

  if (mode === 'draft') {
    return (
      <TrialShell>
        <DraftPanel trial={trial} setTrial={setTrial} onBack={() => setMode(null)} />
      </TrialShell>
    );
  }

  // Render the processing screen in-page while we animate + fetch.
  if (processing) {
    return (
      <TrialShell>
        <ProcessingScreen
          documentName={file?.name || 'document'}
          apiResult={apiResult}
          apiError={apiError}
          onDone={onProcessingDone}
        />
      </TrialShell>
    );
  }

  return (
    <TrialShell>
      <main className="max-w-3xl mx-auto px-6 pt-8 pb-24" data-testid="try-page">
        <div className="text-center space-y-3 mb-10">
          <h1 className="text-3xl sm:text-4xl font-serif font-medium leading-[1.1]">
            Try Bayenah Free
          </h1>
          <p className="text-[14px] text-[#555] leading-relaxed max-w-xl mx-auto">
            Upload a contract and get an instant risk analysis — no account required.
          </p>
          <p className="text-[12.5px] text-[#777]">
            Powered by Saudi law. Results in under 60 seconds.
          </p>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          className={cn(
            'border-2 border-dashed rounded-xl bg-white px-6 py-12 text-center cursor-pointer transition',
            hover ? 'border-[#0A0A0A] bg-[#FAF8F2]' : 'border-[#D8D2C5] hover:border-[#0A0A0A]',
          )}
          data-testid="trial-upload-zone"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="trial-upload-input"
          />
          <div className="mx-auto w-12 h-12 rounded-full bg-[#F5F3EF] border border-[#E5E1D8] flex items-center justify-center mb-4">
            {file ? <FileText className="w-5 h-5 text-[#0A0A0A]" /> : <Upload className="w-5 h-5 text-[#0A0A0A]" />}
          </div>
          <p className="text-[14px] text-[#0A0A0A] font-medium">
            {file ? file.name : 'Drag your contract here or click to upload'}
          </p>
          <p className="text-[12px] text-[#888] mt-1">PDF · DOCX · TXT — Max 10 MB</p>
        </div>

        {error && (
          <p className="text-[12.5px] text-[#B91C1C] mt-3 text-center" data-testid="trial-error">{error}</p>
        )}

        {/* Live document detection chip (Feb 2026) — appears the moment
            extraction begins and updates in real time as the backend
            returns extraction stats and the gpt-4o-mini contract-type
            classification. Pure UI signal that Bayenah understood the
            doc before the user even clicks Analyze. */}
        {file && !error && (() => {
          const isAr = detectedLanguage === 'arabic';
          const stillExtracting = extracting || (!extraction && !detectedType);
          // Build the "type · stats" line in the active language.
          const num = (n) => Number(n || 0).toLocaleString(isAr ? 'ar-EG' : 'en-US');
          const statsParts = [];
          if (extraction?.characters) {
            statsParts.push(isAr
              ? `${num(extraction.characters)} حرف`
              : `${num(extraction.characters)} characters`);
          }
          if (extraction?.tables && extraction.tables > 0) {
            statsParts.push(isAr
              ? `${num(extraction.tables)} ${extraction.tables === 1 ? 'جدول' : 'جداول'}`
              : `${num(extraction.tables)} ${extraction.tables === 1 ? 'table' : 'tables'}`);
          }
          const sectionCount = extraction?.sections || extraction?.paragraphs || 0;
          if (sectionCount > 0) {
            statsParts.push(isAr
              ? `${num(sectionCount)} ${sectionCount === 1 ? 'قسم' : 'أقسام'}`
              : `${num(sectionCount)} ${sectionCount === 1 ? 'section' : 'sections'}`);
          }
          let chipBody;
          if (stillExtracting) {
            chipBody = isAr ? 'جارٍ قراءة الوثيقة...' : 'Reading document…';
          } else if (detectedType) {
            const stats = statsParts.length > 0 ? ' · ' + statsParts.join(' · ') : '';
            chipBody = isAr
              ? `نوع الوثيقة: ${detectedType}${stats}`
              : `Document type detected: ${detectedType}${stats}`;
          } else if (statsParts.length > 0) {
            // Fallback: TXT path or classification timed out — show stats only.
            chipBody = isAr
              ? `${statsParts.join(' · ')} ${'تم استخراجها'}`
              : `${statsParts.join(' · ')} extracted`;
          } else {
            return null;
          }
          return (
            <div
              data-testid="trial-detection-chip"
              dir={isAr ? 'rtl' : 'ltr'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                marginTop: '12px',
                marginInline: 'auto',
                background: '#F5F3EF',
                border: '1px solid #E8E5DE',
                borderRadius: '9999px',
                fontSize: '12px',
                color: stillExtracting ? '#999' : '#555',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                transition: 'color 200ms ease, background 200ms ease',
              }}
              className="block w-fit"
            >
              {stillExtracting ? (
                <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#999', flexShrink: 0 }} />
              ) : (
                <FileText className="w-3 h-3" style={{ color: '#888', flexShrink: 0 }} />
              )}
              <span>{chipBody}</span>
            </div>
          );
        })()}

        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={submit}
            disabled={!file || submitting || extracting}
            className={cn(
              'inline-flex items-center justify-center gap-2 rounded-full text-[14px] px-6 py-3 transition',
              file && !submitting
                ? 'bg-[#0A0A0A] text-white hover:bg-black/85'
                : 'bg-[#0A0A0A]/40 text-white cursor-not-allowed',
            )}
            data-testid="trial-analyze-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {submitting ? 'Analyzing…' : 'Analyze Contract'}
          </button>
        </div>

        <p className="text-[11.5px] text-[#888] text-center mt-6 inline-flex items-center justify-center gap-1.5 w-full">
          <Lock className="w-3 h-3" /> Your document is processed temporarily and never stored.
        </p>
      </main>
    </TrialShell>
  );
}

// ── Multi-agent processing screen (Fix 6) ──────────────────────────
const LAWS_TO_CONSULT = [
  'نظام الشركات',
  'نظام العمل',
  'نظام المعاملات المدنية',
  'نظام المحاكم التجارية',
  'نظام الإثبات',
  'اللوائح التنفيذية',
];

const PROGRESS_MESSAGES = {
  0: 'Reading your document…',
  1: 'Identifying contract type and jurisdiction…',
  2: 'Reviewing clauses against Saudi legal standards…',
  3: 'Searching 25+ Saudi laws and regulations…',
  4: 'Calculating risk scores…',
  5: 'Checking compliance requirements…',
  6: 'Generating your report…',
};

const STEP_DURATIONS = [400, 400, 600, 600, 500, 500, 500]; // ms — sums to 3500ms (Fix 5)

function ProcessingScreen({ documentName, apiResult, apiError, onDone }) {
  // Derive counts from the real API response if it has landed, otherwise
  // use sensible placeholder values so the details read naturally.
  const p = apiResult?.partial_analysis || {};
  const clauseCount        = p.clauses_reviewed ?? 12;
  const pageCount          = Math.max(1, Math.round(clauseCount / 6));
  const contractType       = 'Legal Contract';
  const governingLawDetected = p.governing_law || 'Saudi law';
  const sourceCount        = 6;
  const lawCount           = 4;
  const riskCount          = (p.top_risks?.length ?? 0) + (p.missing_clauses_count ?? 3);
  const criticalCount      = Math.min(p.top_risks?.length ?? 2, 2);
  const mediumCount        = Math.max(0, riskCount - criticalCount);
  const missingCount       = p.missing_clauses_count ?? 4;

  const steps = useMemo(() => ([
    { title: 'Document Parsing',        detail: `Extracted ${clauseCount} clauses across ${pageCount} sections`,          agent: 'parser-agent' },
    { title: 'Contract Classification', detail: `Identified as: ${contractType} — ${governingLawDetected}`,               agent: 'classifier-agent' },
    { title: 'Clause-by-Clause Review', detail: `Reviewed ${clauseCount} clauses against Saudi legal standards`,          agent: 'review-agent' },
    { title: 'Saudi Law Corpus Search', detail: `Retrieved ${sourceCount} relevant articles from ${lawCount} regulations`, agent: 'rag-agent' },
    { title: 'Risk Assessment',         detail: `Identified ${riskCount} risks — ${criticalCount} critical, ${mediumCount} medium`, agent: 'risk-agent' },
    { title: 'Compliance Gap Analysis', detail: `Found ${missingCount} missing clauses required under Saudi law`,         agent: 'compliance-agent' },
    { title: 'Report Generation',       detail: 'Compiling findings with citations and recommendations',                  agent: 'report-agent' },
  ]), [clauseCount, pageCount, contractType, governingLawDetected, sourceCount, lawCount, riskCount, criticalCount, mediumCount, missingCount]);

  const [activeStep, setActiveStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [chips, setChips] = useState([]); // laws revealed during RAG step

  // Sequential step timer.
  useEffect(() => {
    if (finished) return;
    if (activeStep >= steps.length) { setFinished(true); return; }
    const t = setTimeout(() => setActiveStep((s) => s + 1), STEP_DURATIONS[activeStep]);
    return () => clearTimeout(t);
  }, [activeStep, finished, steps.length]);

  // Fix 5: if the API lands before the animation finishes, jump straight
  // to the last step so users see results without waiting on the timer.
  useEffect(() => {
    if (!apiResult && !apiError) return;
    if (finished) return;
    setActiveStep(steps.length);
    setFinished(true);
  }, [apiResult, apiError, finished, steps.length]);

  // Reveal law chips one-by-one while the RAG step (index 3) is active.
  useEffect(() => {
    if (activeStep !== 3) return;
    const intervals = LAWS_TO_CONSULT.map((law, i) =>
      setTimeout(() => setChips((c) => (c.includes(law) ? c : [...c, law])), i * 300),
    );
    return () => intervals.forEach(clearTimeout);
  }, [activeStep]);

  // Final handoff: wait until BOTH the animation has finished AND the
  // API call has resolved (success or error). Fix 5: short 300ms beat
  // (was 800ms) so users land on results faster once everything is ready.
  useEffect(() => {
    if (!finished) return;
    if (!apiResult && !apiError) return; // still waiting on API
    const t = setTimeout(onDone, 300);
    return () => clearTimeout(t);
  }, [finished, apiResult, apiError, onDone]);

  const progress = Math.min(100, Math.round(((finished ? steps.length : activeStep) / steps.length) * 100));
  const apiLanded = Boolean(apiResult || apiError);
  const progressMessage = finished && !apiLanded
    ? 'Finalizing…'
    : (PROGRESS_MESSAGES[Math.min(activeStep, steps.length - 1)] || 'Generating your report…');

  return (
    <>
      <style>{`
        @keyframes trial-spin { to { transform: rotate(360deg); } }
        @keyframes trial-fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes trial-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
      `}</style>
      <div
        data-testid="trial-processing"
        style={{
          minHeight: 'calc(100vh - 64px)',
          background: '#F5F3EF',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '13px', color: '#999999', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
            Analyzing your contract
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 500, color: '#111111', margin: 0 }} data-testid="trial-processing-docname">
            {cleanDocumentName(documentName)}
          </h2>
        </div>

        {/* Pipeline */}
        <div
          style={{
            width: '100%', maxWidth: '560px', background: '#FFFFFF',
            border: '1px solid #E8E8E8', borderRadius: '12px', padding: '24px', marginBottom: '32px',
          }}
          data-testid="trial-processing-pipeline"
        >
          <div style={{ fontSize: '11px', color: '#999999', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px' }}>
            Multi-Agent Analysis Pipeline
          </div>
          {steps.map((step, index) => {
            const status = index < activeStep ? 'complete' : index === activeStep ? 'active' : 'pending';
            return (
              <div
                key={index}
                data-testid={`trial-processing-step-${index}`}
                data-status={status}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '12px 0',
                  borderBottom: index < steps.length - 1 ? '1px solid #F5F5F5' : 'none',
                  opacity: status === 'pending' ? 0.3 : 1, transition: 'opacity 0.5s ease',
                }}
              >
                <div style={{ marginTop: '2px', flexShrink: 0 }}>
                  {status === 'complete' && (
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#111111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                  {status === 'active' && (
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #111111', borderTopColor: 'transparent', animation: 'trial-spin 0.8s linear infinite' }} />
                  )}
                  {status === 'pending' && (
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #E8E8E8' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#111111', marginBottom: '2px' }}>
                    {step.title}
                  </div>
                  {status !== 'pending' && (
                    <div style={{ fontSize: '12px', color: '#888888', lineHeight: 1.5 }}>
                      {step.detail}
                    </div>
                  )}
                </div>
                {status !== 'pending' && (
                  <div style={{
                    fontSize: '10px', color: '#999999', background: '#F5F5F5',
                    border: '1px solid #E8E8E8', borderRadius: '4px', padding: '2px 8px',
                    flexShrink: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}>
                    {step.agent}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Consulted laws */}
        <div style={{ width: '100%', maxWidth: '560px', marginBottom: '24px' }} data-testid="trial-processing-laws">
          <div style={{ fontSize: '11px', color: '#999999', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px', textAlign: 'center' }}>
            Consulting Saudi Law Corpus
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', minHeight: '32px' }}>
            {chips.map((law, i) => (
              <div key={i} style={{
                padding: '4px 12px', background: '#FFFFFF', border: '1px solid #E8E8E8',
                borderRadius: '9999px', fontSize: '12px', color: '#444444',
                animation: 'trial-fadeIn 0.4s ease', display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#111111' }} />
                {law}
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', maxWidth: '560px', height: '3px', background: '#E8E8E8', borderRadius: '2px', overflow: 'hidden' }}>
          <div
            data-testid="trial-processing-progress"
            data-progress={progress}
            style={{ height: '100%', background: '#111111', borderRadius: '2px', width: `${progress}%`, transition: 'width 0.6s ease' }}
          />
        </div>
        <div
          style={{ fontSize: '12px', color: '#BBBBBB', marginTop: '12px', animation: finished && !apiLanded ? 'trial-pulse 1.4s ease-in-out infinite' : 'none' }}
          data-testid="trial-processing-message"
        >
          {progressMessage}
        </div>
      </div>
    </>
  );
}

// ── Results page ────────────────────────────────────────────────────
export function TryResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const result = location.state?.result;
  const docName = location.state?.document_name;
  const extraction = location.state?.extraction || null;

  // Fade-in on mount — earned after the processing screen.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 30); return () => clearTimeout(t); }, []);

  // If user lands here directly (refresh or pasted URL) bounce back.
  useEffect(() => {
    if (!result) navigate('/try', { replace: true });
  }, [result, navigate]);

  const partial = result?.partial_analysis || {};
  const missingClausesCount = partial.missing_clauses_count ?? 0;
  const isArabic = (result?.language || partial.language) === 'arabic';
  // Fix 3: prefer detected contract type from analysis; fall back to cleaned filename.
  const headerTitle = (partial.contract_type && String(partial.contract_type).trim())
    || cleanDocumentName(docName);
  // Fix 1: bilingual page chrome — header label, section titles, and CTA copy.
  const t = isArabic ? {
    header: 'تحليل عقدك',
    extractedSuffix: 'تم استخراجها',
    verdict: 'الخلاصة',
    riskScore: 'درجة المخاطر',
    riskLevel: 'مستوى المخاطر',
    topRisks: 'أبرز المخاطر',
    noRisks: 'لم تظهر مخاطر محددة في هذا العقد.',
    clausesReviewed: 'البنود المراجعة',
    missingFound: 'البنود الناقصة',
    governingLaw: 'القانون الحاكم',
    fullReport: 'التقرير الكامل',
    lockedHeader: 'مغلق في التقرير الكامل',
    lockedItems: [
      `تفصيل كامل لجميع البنود الناقصة (${missingClausesCount})`,
      'كل المخاطر بعد أبرز اثنتين',
      'توصيات محددة لمعالجة كل مشكلة',
      'مراجع الأنظمة السعودية بأرقام المواد',
      'تقرير قابل للتنزيل (.docx)',
    ],
    readyTitle: 'تقريرك الكامل جاهز.',
    readyBody: (n) => (<>انضم إلى قائمة الانتظار لفتح التحليل الكامل — جميع <strong>{n}</strong> البنود الناقصة، وكل المخاطر، وتوصيات محددة لكل مشكلة.</>),
    fullName: 'الاسم الكامل',
    workEmail: 'البريد الإلكتروني للعمل (شركاتي فقط)',
    companyName: 'اسم الشركة',
    cta: 'احصل على التقرير الكامل + انضم لقائمة الانتظار',
    sending: 'جارٍ الإرسال…',
    note: 'تم تحليل وثيقتك بالفعل. سيُرسل التقرير الكامل إلى بريدك فوراً.',
    sentSuccess: 'تم إرسال التقرير الكامل إلى',
    redirecting: 'جارٍ التحويل…',
  } : {
    header: 'Your contract analysis',
    extractedSuffix: 'extracted',
    verdict: 'Verdict',
    riskScore: 'Risk score',
    riskLevel: 'Risk level',
    topRisks: 'Top Risks Identified',
    noRisks: 'No specific risks surfaced in this contract.',
    clausesReviewed: 'Clauses reviewed',
    missingFound: 'Missing clauses found',
    governingLaw: 'Governing law',
    fullReport: 'Full Report',
    lockedHeader: 'Locked in full report',
    lockedItems: [
      `Full breakdown of all ${missingClausesCount} missing clauses`,
      'All identified risks beyond the top 2',
      'Specific fix recommendations for each issue',
      'Saudi law citations with article numbers',
      'Downloadable report (.docx)',
    ],
    readyTitle: 'Your full report is ready.',
    readyBody: (n) => (<>Join the waitlist to unlock the complete analysis — all <strong>{n}</strong> missing clauses, every risk, and specific recommendations for each issue found.</>),
    fullName: 'Full name',
    workEmail: 'Work email (corporate only)',
    companyName: 'Company name',
    cta: 'Get Full Report + Join Waitlist',
    sending: 'Sending…',
    note: 'Your document has already been analyzed. The full report will be emailed to you immediately.',
    sentSuccess: '✓ Full report sent to',
    redirecting: 'Redirecting…',
  };

  const [form, setForm] = useState({ full_name: '', work_email: '', company_name: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const valid = useMemo(() => (
    form.full_name.trim().length >= 2 &&
    form.work_email.includes('@') &&
    form.company_name.trim().length >= 2
  ), [form]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const unlock = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    if (!isCorporate(form.work_email)) {
      setError('Please use your corporate email address.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/trial/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trial_id: result.trial_id, ...form }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Unlock failed.');
      setDone(true);
      setTimeout(() => navigate('/waitlist', {
        state: {
          fromTrial: true,
          waitlistData: { ...form, work_email: form.work_email.toLowerCase(), company_domain: data.company_domain },
        },
      }), 1500);
    } catch (e2) {
      setError(e2.message || 'Could not save your details. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [form, result, navigate]);

  if (!result) return null;

  return (
    <TrialShell>
      <main
        className="max-w-3xl mx-auto px-6 pt-6 pb-24"
        data-testid="try-results-page"
        dir={isArabic ? 'rtl' : 'ltr'}
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 500ms ease, transform 500ms ease',
        }}
      >
        {/* Fix 3: detected contract type fallback to cleaned filename */}
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-[#666] mb-2" data-testid="trial-results-header">
          {t.header} · {headerTitle}
        </p>

        {/* Extraction-stats chip — transparency signal that the whole doc was read */}
        {(() => {
          const parts = formatExtractionParts(extraction, isArabic);
          if (!parts) return null;
          return (
            <div
              data-testid="trial-extraction-stats"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 12px',
                marginBottom: '16px',
                background: '#F5F3EF',
                border: '1px solid #E8E5DE',
                borderRadius: '9999px',
                fontSize: '11.5px',
                color: '#777',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
              }}
            >
              <FileText className="w-3 h-3" style={{ color: '#999', flexShrink: 0 }} />
              <span>
                {parts.join(isArabic ? ' · ' : ' · ')} {t.extractedSuffix}
              </span>
            </div>
          );
        })()}

        {/* Verdict banner */}
        <div
          className="rounded-xl border border-[#E5E1D8] bg-white p-5 grid grid-cols-3 gap-4 items-center"
          data-testid="trial-verdict"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#888] mb-1">{t.verdict}</div>
            <div className="font-serif text-[22px] font-medium text-[#0A0A0A]">{partial.verdict || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#888] mb-1">{t.riskScore}</div>
            <div className="font-serif text-[28px] font-medium text-[#0A0A0A]">
              {partial.risk_score ?? '—'}<span className="text-[14px] text-[#888]">/10</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#888] mb-1">{t.riskLevel}</div>
            <div className="font-serif text-[22px] font-medium text-[#0A0A0A]">{partial.risk_level || '—'}</div>
          </div>
        </div>

        {/* Top 2 risks */}
        <section className="mt-8" data-testid="trial-top-risks">
          <h3 className="text-[14px] font-medium text-[#0A0A0A] mb-3">{t.topRisks}</h3>
          {(partial.top_risks || []).length === 0 ? (
            <p className="text-[13px] text-[#666]">{t.noRisks}</p>
          ) : (
            <ul className="space-y-2">
              {(partial.top_risks || []).map((risk, i) => (
                <li key={i} className="flex items-start gap-3 text-[13.5px] text-[#222] leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#0A0A0A] shrink-0" />
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Stats row */}
        <div className="mt-8 grid grid-cols-3 gap-4" data-testid="trial-stats">
          <Stat number={partial.clauses_reviewed ?? 0} label={t.clausesReviewed} />
          <Stat number={missingClausesCount} label={t.missingFound} />
          <Stat number={partial.governing_law || '—'} label={t.governingLaw} small />
        </div>

        {/* Fix 4: gate divider */}
        <div
          data-testid="trial-gate-divider"
          style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 16px' }}
        >
          <div style={{ flex: 1, height: '1px', background: '#E8E8E8' }} />
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px',
            border: '1px solid #E8E8E8', borderRadius: '9999px',
            fontSize: '11px', color: '#999999', background: '#FFFFFF', whiteSpace: 'nowrap',
          }}>
            <Lock className="w-3 h-3" /> {t.fullReport}
          </div>
          <div style={{ flex: 1, height: '1px', background: '#E8E8E8' }} />
        </div>

        {/* Fix 2: "Locked in full report" teaser */}
        <div
          data-testid="trial-locked-teaser"
          style={{
            border: '1px solid #E8E8E8', borderRadius: '8px', padding: '16px 20px',
            marginBottom: '16px', background: '#FFFFFF',
          }}
        >
          <div style={{ fontSize: '12px', color: '#999999', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t.lockedHeader}
          </div>
          {t.lockedItems.map((item, i, arr) => (
            <div
              key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0',
                borderBottom: i < arr.length - 1 ? '1px solid #F5F5F5' : 'none',
                fontSize: '13px', color: '#444444',
              }}
            >
              <Lock className="w-3.5 h-3.5" style={{ color: '#999999', flexShrink: 0 }} />
              <span>{item}</span>
            </div>
          ))}
        </div>

        {/* Gate wall — blurred preview + unlock form */}
        <section
          className="rounded-xl bg-[#F5F3EF] border border-[#E8E8E8] p-8 text-center"
          data-testid="trial-gate-wall"
        >
          {/* Fix 4: 8-row blurred preview, slightly more visible (4px blur, 0.25 opacity) */}
          <div
            aria-hidden="true"
            data-testid="trial-blurred-preview"
            style={{
              filter: 'blur(4px)', opacity: 0.25, pointerEvents: 'none',
              background: '#FFFFFF', borderRadius: '8px', padding: '16px 20px',
              marginBottom: '20px', border: '1px solid #E8E8E8',
              width: '100%', maxWidth: '480px', margin: '0 auto 20px',
            }}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', gap: '10px', padding: '8px 0',
                  borderBottom: i < 7 ? '1px solid #F5F5F5' : 'none',
                  alignItems: 'center',
                }}
              >
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: i < 2 ? '#E24B4A' : i < 5 ? '#EF9F27' : '#639922',
                  flexShrink: 0,
                }} />
                <div style={{
                  height: '10px', background: '#E8E8E8', borderRadius: '4px',
                  width: `${55 + (i * 7) % 35}%`,
                }} />
                <div style={{
                  height: '20px', background: '#F0F0F0', borderRadius: '4px',
                  width: '50px', marginLeft: 'auto', flexShrink: 0,
                }} />
              </div>
            ))}
          </div>

          <h2 className="font-serif text-[22px] font-medium text-[#111]">{t.readyTitle}</h2>
          <p className="text-[13.5px] text-[#666] leading-relaxed max-w-md mx-auto mt-2">
            {t.readyBody(missingClausesCount)}
          </p>

          {!done ? (
            <form onSubmit={unlock} className="max-w-md mx-auto mt-6 space-y-3" data-testid="trial-unlock-form">
              <input
                value={form.full_name} onChange={set('full_name')} placeholder={t.fullName}
                className="w-full rounded-lg border border-[#E0E0E0] bg-white px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A]"
                data-testid="trial-unlock-name" required
              />
              <input
                type="email" value={form.work_email} onChange={set('work_email')}
                placeholder={t.workEmail} autoComplete="email"
                className="w-full rounded-lg border border-[#E0E0E0] bg-white px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A]"
                data-testid="trial-unlock-email" required
              />
              <input
                value={form.company_name} onChange={set('company_name')} placeholder={t.companyName}
                className="w-full rounded-lg border border-[#E0E0E0] bg-white px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A]"
                data-testid="trial-unlock-company" required
              />
              {error && <p className="text-[12.5px] text-[#B91C1C]" data-testid="trial-unlock-error">{error}</p>}
              {/* Fix 2: button is ALWAYS solid black on the results page.
                  By the time we render here the user has already seen results,
                  so disabled state should not paint it gray. The button only
                  prevents submission via the `disabled` attribute. */}
              <button
                type="submit"
                disabled={!valid || busy}
                data-testid="trial-unlock-submit"
                style={{
                  background: '#111111',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '14px 24px',
                  fontSize: '14px',
                  fontWeight: 500,
                  width: '100%',
                  opacity: 1,
                  cursor: valid && !busy ? 'pointer' : 'not-allowed',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background 150ms ease',
                }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {busy ? t.sending : t.cta}
              </button>
              <p className="text-[11px] text-[#999] mt-2">
                {t.note}
              </p>
            </form>
          ) : (
            <div className="mt-6 text-[13.5px] text-[#0A0A0A]" data-testid="trial-unlock-success">
              {t.sentSuccess} <strong>{form.work_email}</strong>. {t.redirecting}
            </div>
          )}
        </section>
      </main>
    </TrialShell>
  );
}

function Stat({ number, label, small }) {
  return (
    <div className="rounded-xl bg-white border border-[#E5E1D8] p-4 flex flex-col items-start" data-testid={`trial-stat-${label.toLowerCase().split(' ').join('-')}`}>
      <span
        className="font-serif font-medium text-[#0A0A0A] leading-none mb-2"
        style={{ fontSize: small ? '20px' : '36px' }}
      >
        {number}
      </span>
      <span className="text-[11.5px] text-[#666]">{label}</span>
    </div>
  );
}
